import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HiMenu, HiX } from "react-icons/hi";
import { FaSignOutAlt, FaTachometerAlt, FaSignInAlt, FaUserCircle, FaChevronDown } from "react-icons/fa";
import logo from "../../assets/arvix/arvix_logo.png";
import { navLinks } from "../../config/constants";
import { useAuth } from "../../context/AuthContext";

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, isTeacher, isStudent, user, logout } = useAuth();

    const dashboardPath = isTeacher ? '/dashboard/teacher' : '/dashboard/student';
    const roleLabel = isTeacher ? 'Teacher' : isStudent ? 'Student' : null;
    const isAdminRoute = location.pathname.startsWith('/admin');

    const handleLogout = () => {
        logout();
        setIsOpen(false);
        setDropdownOpen(false);
        navigate('/');
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    return (
        <div
            className="fixed inset-x-0 top-3 sm:top-4 z-50 px-4 sm:px-6 lg:px-8"
            role="navigation"
            aria-label="Main navigation"
        >
            <div className="container mx-auto">
                {/* Floating pill */}
                <nav
                    className={`rounded-2xl border transition-all duration-500 ${
                        scrolled || isOpen
                            ? "border-designer-dark/10 bg-white/90 shadow-[0_20px_50px_-20px_rgba(29,32,48,0.4)] backdrop-blur-xl"
                            : "border-designer-dark/[0.07] bg-white/70 shadow-[0_12px_35px_-18px_rgba(29,32,48,0.3)] backdrop-blur-lg"
                    }`}
                >
                    <div className="flex h-14 md:h-16 items-center justify-between gap-3 pl-3 pr-2 sm:pl-4 sm:pr-3">
                        {/* Logo */}
                        <Link
                            to="/"
                            className="group flex shrink-0 items-center rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                            aria-label="Arvix Creative - Art Vision Experience - Creative Studio Home"
                        >
                            <img
                                src={logo}
                                alt="Arvix Creative logo"
                                className="h-8 md:h-9 w-auto transition-transform duration-500 group-hover:scale-[1.05]"
                                loading="eager"
                                decoding="async"
                            />
                        </Link>

                        {/* Desktop links */}
                        {!isAdminRoute && (
                            <div className="hidden items-center gap-0.5 lg:flex">
                                {navLinks.map((link) => {
                                    const active = location.pathname === link.path;
                                    return (
                                        <Link
                                            key={link.name}
                                            to={link.path}
                                            aria-current={active ? "page" : undefined}
                                            className={`relative rounded-full px-3.5 py-2 text-[13px] font-semibold tracking-wide transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/40 ${
                                                active
                                                    ? "bg-designer-dark text-white"
                                                    : "text-designer-dark/75 hover:bg-designer-dark/[0.06] hover:text-designer-dark"
                                            }`}
                                        >
                                            {link.name}
                                            {link.badge && (
                                                <span
                                                    className="absolute -top-1 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-1.5 py-[1px] text-[9px] font-bold leading-none tracking-wide text-white shadow-sm"
                                                    aria-hidden="true"
                                                >
                                                    {link.badge}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}

                        {/* Right cluster */}
                        <div className="flex items-center gap-2">
                            {!isAdminRoute && (
                                <div className="hidden lg:block">
                                    {isAuthenticated ? (
                                        <div className="relative" ref={dropdownRef}>
                                            <button
                                                onClick={() => setDropdownOpen((p) => !p)}
                                                className="flex items-center gap-2 rounded-full border border-designer-dark/15 py-1 pl-1 pr-2.5 transition-all duration-200 hover:border-designer-primary/50 hover:bg-red-50/50"
                                                aria-label="User menu"
                                                aria-expanded={dropdownOpen}
                                            >
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-designer-primary text-sm font-bold text-white">
                                                    {user?.name?.[0]?.toUpperCase() || <FaUserCircle className="h-4 w-4" />}
                                                </span>
                                                <span className="hidden max-w-[90px] truncate text-[13px] font-semibold text-designer-dark xl:block">
                                                    {user?.name}
                                                </span>
                                                <FaChevronDown className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {dropdownOpen && (
                                                <div className="absolute right-0 top-full z-50 mt-3 w-64 animate-fade-in overflow-hidden rounded-2xl border border-designer-dark/10 bg-white shadow-[0_24px_60px_-20px_rgba(29,32,48,0.35)]">
                                                    <div className="border-b border-designer-dark/10 bg-designer-dark/[0.03] px-4 py-4">
                                                        <div className="flex items-start gap-3">
                                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-designer-primary text-base font-bold text-white">
                                                                {user?.name?.[0]?.toUpperCase() || '?'}
                                                            </span>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <p className="truncate text-sm font-bold text-designer-dark">{user?.name}</p>
                                                                    {roleLabel && (
                                                                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide ${
                                                                            isTeacher
                                                                                ? 'border-blue-100 bg-blue-50 text-blue-600'
                                                                                : 'border-green-100 bg-green-50 text-green-600'
                                                                        }`}>
                                                                            {roleLabel}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="mt-0.5 truncate text-xs text-gray-400">{user?.email}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="p-2">
                                                        <Link
                                                            to={dashboardPath}
                                                            onClick={() => setDropdownOpen(false)}
                                                            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-designer-primary"
                                                        >
                                                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 transition-colors group-hover:bg-red-100">
                                                                <FaTachometerAlt className="h-3.5 w-3.5 text-gray-400 group-hover:text-designer-primary" />
                                                            </span>
                                                            Dashboard
                                                        </Link>
                                                    </div>
                                                    <div className="px-2 pb-2">
                                                        <div className="mb-2 h-px bg-gray-100" />
                                                        <button
                                                            onClick={handleLogout}
                                                            className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
                                                            aria-label="Sign out"
                                                        >
                                                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 transition-colors group-hover:bg-red-100">
                                                                <FaSignOutAlt className="h-3.5 w-3.5 text-red-400 group-hover:text-red-500" />
                                                            </span>
                                                            Sign Out
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <Link
                                                to="/login"
                                                className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold text-designer-dark/80 transition-colors hover:bg-designer-dark/[0.06] hover:text-designer-dark"
                                            >
                                                <FaSignInAlt className="h-3.5 w-3.5" />
                                                Login
                                            </Link>
                                            <Link
                                                to="/contact"
                                                className="flex items-center gap-1.5 rounded-full bg-designer-primary px-5 py-2.5 text-[13px] font-bold text-white shadow-lg shadow-red-500/30 transition-all duration-300 hover:bg-red-700"
                                            >
                                                Start a Project
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Mobile toggle */}
                            {!isAdminRoute && (
                                <button
                                    className="flex h-10 w-10 items-center justify-center rounded-xl text-designer-dark transition-colors hover:bg-designer-dark/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50 lg:hidden"
                                    onClick={() => setIsOpen(!isOpen)}
                                    aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
                                    aria-expanded={isOpen}
                                    aria-controls="mobile-menu"
                                >
                                    {isOpen ? <HiX size={22} aria-hidden="true" /> : <HiMenu size={22} aria-hidden="true" />}
                                </button>
                            )}
                        </div>
                    </div>
                </nav>

                {/* Mobile dropdown card */}
                {!isAdminRoute && (
                    <div
                        id="mobile-menu"
                        aria-hidden={!isOpen}
                        className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${
                            isOpen ? "mt-2 max-h-[calc(100dvh-120px)] opacity-100 overflow-y-auto" : "max-h-0 opacity-0"
                        }`}
                    >
                        <div className="rounded-2xl border border-designer-dark/10 bg-white/95 p-2 shadow-[0_24px_60px_-20px_rgba(29,32,48,0.4)] backdrop-blur-xl">
                            {navLinks.map((link, i) => {
                                const active = location.pathname === link.path;
                                return (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        onClick={() => setIsOpen(false)}
                                        aria-current={active ? "page" : undefined}
                                        className={`flex items-center justify-between rounded-xl px-4 py-3 transition-colors ${
                                            active ? "bg-designer-dark text-white" : "text-designer-dark hover:bg-designer-dark/[0.05]"
                                        } ${i > 0 ? "mt-0.5" : ""}`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className={`font-mono text-[11px] ${active ? "text-white/50" : "text-gray-300"}`}>
                                                0{i + 1}
                                            </span>
                                            <span className="font-display text-[17px] font-bold">{link.name}</span>
                                            {link.badge && (
                                                <span className="rounded-full bg-emerald-500 px-1.5 py-[1px] text-[9px] font-bold leading-none tracking-wide text-white">
                                                    {link.badge}
                                                </span>
                                            )}
                                        </span>
                                        <span className={`text-lg leading-none ${active ? "text-white" : "text-gray-300"}`} aria-hidden="true">
                                            →
                                        </span>
                                    </Link>
                                );
                            })}

                            <div className="mt-2 border-t border-designer-dark/10 p-2">
                                {isAuthenticated ? (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-3 px-2 py-2">
                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-designer-primary text-base font-bold text-white">
                                                {user?.name?.[0]?.toUpperCase() || '?'}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <p className="truncate text-sm font-bold text-designer-dark">{user?.name}</p>
                                                    {roleLabel && (
                                                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide ${
                                                            isTeacher
                                                                ? 'border-blue-100 bg-blue-50 text-blue-600'
                                                                : 'border-green-100 bg-green-50 text-green-600'
                                                        }`}>
                                                            {roleLabel}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-0.5 truncate text-xs text-gray-400">{user?.email}</p>
                                            </div>
                                        </div>
                                        <Link
                                            to={dashboardPath}
                                            onClick={() => setIsOpen(false)}
                                            className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-designer-dark hover:bg-designer-dark/[0.05]"
                                        >
                                            <FaTachometerAlt className="h-4 w-4 text-designer-primary" />
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-red-500 hover:bg-red-50"
                                        >
                                            <FaSignOutAlt className="h-4 w-4" />
                                            Sign Out
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2.5">
                                        <Link
                                            to="/login"
                                            onClick={() => setIsOpen(false)}
                                            className="flex items-center justify-center gap-2 rounded-full border border-designer-dark/20 px-4 py-3 text-sm font-semibold text-designer-dark"
                                        >
                                            <FaSignInAlt className="h-3.5 w-3.5" />
                                            Login
                                        </Link>
                                        <Link
                                            to="/register"
                                            onClick={() => setIsOpen(false)}
                                            className="flex items-center justify-center gap-2 rounded-full bg-designer-primary px-4 py-3 text-sm font-semibold text-white"
                                        >
                                            <FaUserCircle className="h-4 w-4" />
                                            Register
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Navbar;
